import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance for admin API calls
const adminAPI = axios.create({
  baseURL: `${API_BASE_URL}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
adminAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
adminAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminData');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export const adminService = {
  // ====================================
  // AUTHENTICATION
  // ====================================
  login: (credentials) => adminAPI.post('/auth/login', credentials),
  logout: () => adminAPI.post('/auth/logout'),

  // ====================================
  // USER MANAGEMENT
  // ====================================
  getAllUsers: (params = {}) => adminAPI.get('/users', { params }),
  getUserById: (userId) => adminAPI.get(`/users/${userId}`),
  restoreUser: (userId) => adminAPI.patch(`/users/${userId}/restore`),
  activateUser: (userId) => adminAPI.patch(`/users/${userId}/activate`),
  deactivateUser: (userId, reason) => adminAPI.patch(`/users/${userId}/deactivate`, { reason }),
  verifyUser: (userId) => adminAPI.patch(`/users/${userId}/verify`),
  deleteUser: (userId, reason) => adminAPI.delete(`/users/${userId}`, { data: { reason } }),

  // ====================================
  // SUBSCRIPTION MANAGEMENT
  // ====================================
  getAllSubscriptions: (params = {}) => adminAPI.get('/subscriptions', { params }),
  getUserSubscription: (userId) => adminAPI.get(`/users/${userId}/subscription`),
  updateUserSubscription: (userId, subscriptionData) => 
    adminAPI.post(`/users/${userId}/subscription`, subscriptionData),
  changeUserPackage: (userId, packageData) => 
    adminAPI.patch(`/users/${userId}/subscription/package`, packageData),
  updateSubscriptionDates: (userId, dates) => 
    adminAPI.patch(`/users/${userId}/subscription/dates`, dates),

  // ====================================
  // PAYMENT MANAGEMENT
  // ====================================
  getAllPayments: (params = {}) => adminAPI.get('/payments', { params }),
  getPaymentStats: () => adminAPI.get('/payments/stats'),
  getGeideaPayments: (params = {}) => adminAPI.get('/payments/geidea', { params }),
  getPaymentById: (transactionId) => adminAPI.get(`/payments/${transactionId}`),
  updatePaymentStatus: (transactionId, statusData) => 
    adminAPI.patch(`/payments/${transactionId}/status`, statusData),

  // ====================================
  // DASHBOARD & ANALYTICS
  // ====================================
  getDashboardStats: () => adminAPI.get('/dashboard/stats'),
  getRecentActivity: (params = {}) => adminAPI.get('/dashboard/activity', { params }),

  // ====================================
  // SYSTEM MONITORING
  // ====================================
  getSystemHealth: () => adminAPI.get('/health'),
  getSystemStats: () => adminAPI.get('/stats'),
  getSystemTokens: () => adminAPI.get('/tokens'),
  getSystemData: () => adminAPI.get('/data'),

  getAdminProfile: () => adminAPI.get('/profile'),
  changePassword: (passwordData) => adminAPI.patch('/profile/password', passwordData),
  changeEmail: (emailData) => adminAPI.patch('/profile/email', emailData),
};

export default adminService; 